// Party Subscription API Service
// Endpoints: /api/v1/party-subscriptions
// Refactored: Return config methods removed. Subscriptions now link directly to items.

import axiosInstance from '@/app/api/axiosInstance';
import {
  PartySubscription,
  PartySubscriptionCreate,
  PartySubscriptionUpdate,
  SubscriptionStatus,
} from '@/app/types/compliance';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

const API_PATH = '/party-subscriptions';

// ============== Party Subscriptions ==============

/**
 * List all party subscriptions with optional filtering
 */
export const listPartySubscriptions = async (
  params: ListQueryParams & {
    party_id?: number;
    compliance_service_id?: number;
    item_id?: number;
    status?: SubscriptionStatus;
    assigned_user_id?: number;
  }
): Promise<PaginatedResponse<PartySubscription>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<PartySubscription>>(
      `${API_PATH}/`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to list party subscriptions:', error);
    throw error;
  }
};

/**
 * Get a single party subscription by ID
 */
export const getPartySubscription = async (id: number): Promise<PartySubscription> => {
  try {
    const response = await axiosInstance.get<PartySubscription>(`${API_PATH}/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get party subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new party subscription
 */
export const createPartySubscription = async (
  data: PartySubscriptionCreate
): Promise<PartySubscription> => {
  try {
    const response = await axiosInstance.post<PartySubscription>(`${API_PATH}/`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create party subscription:', error);
    throw error;
  }
};

/**
 * Update an existing party subscription
 */
export const updatePartySubscription = async (
  id: number,
  data: PartySubscriptionUpdate
): Promise<PartySubscription> => {
  try {
    const response = await axiosInstance.patch<PartySubscription>(`${API_PATH}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update party subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a party subscription
 */
export const deletePartySubscription = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`${API_PATH}/${id}`);
  } catch (error) {
    console.error(`Failed to delete party subscription ${id}:`, error);
    throw error;
  }
};

// ============== Convenience Functions ==============

/**
 * Get all subscriptions for a specific party
 */
export const getPartySubscriptionsForParty = async (
  partyId: number,
  status?: SubscriptionStatus
): Promise<PaginatedResponse<PartySubscription>> => {
  return listPartySubscriptions({
    party_id: partyId,
    status,
    limit: 100,
  });
};

/**
 * Get all subscriptions for a specific item
 */
export const getPartySubscriptionsForItem = async (
  itemId: number,
  status?: SubscriptionStatus
): Promise<PaginatedResponse<PartySubscription>> => {
  return listPartySubscriptions({
    item_id: itemId,
    status,
    limit: 100,
  });
};
